import {
  Alert,
  Box,
  Grid,
  Modal,
  ModalContent,
  RadioButtonList,
  SelectInput,
  TextInput
} from "@dealer-center/core-components";
import { useUpdateDepartment } from "@dealer-center/ddc-pages-departments-fetch";
import { useOutletContext } from "react-router-dom";
import { defaultDepartments } from "./defaultDepartments";
import { useState } from "react";
import {
  labelToType,
  parseErrorMessages,
  sanitizeLabel,
  validateDepartment
} from "./utils";

import type { AccountEntity } from "@dealer-center/ddc-api-account-interface";
import { type DepartmentTypeOptions, useValidateDepartment } from "../hooks";
import type { Department } from "@dealer-center/ddc-pages-departments-interface";
import type { EditDepartmentModal } from "../Departments";

interface EditDepartmentProps extends EditDepartmentModal {
  hideModal: () => void;
  departments: Department[];
  userId?: string;
}

export const EditDepartment = ({
  hideModal,
  modalToShow,
  selected,
  departments,
  userId
}: EditDepartmentProps) => {
  const mutation = useUpdateDepartment();
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const { accountEntity } = useOutletContext<{
    accountEntity: AccountEntity;
  }>();

  const isUsingAPreConfiguredDepartment = defaultDepartments.some(
    department => department.value === selected.type
  );

  const [departmentType, setDepartmentType] = useState<DepartmentTypeOptions>(
    isUsingAPreConfiguredDepartment ? "pre-configured" : "custom"
  );

  const [preConfiguredDepartment, setPreConfiguredDepartment] = useState(
    isUsingAPreConfiguredDepartment ? selected.type : ""
  );

  const [customDepartmentLabel, setCustomDepartmentLabel] = useState(
    !isUsingAPreConfiguredDepartment ? selected.label : ""
  );

  const customDepartmentType = customDepartmentLabel
    ? labelToType(customDepartmentLabel)
    : "";

  const existingTypes = departments
    .filter(d => d.type !== selected.type)
    .map(d => d.type.toUpperCase());

  const defaultDepartmentTypes = defaultDepartments.map(d =>
    d.value.toUpperCase()
  );

  const defaultDepartmentOptions = defaultDepartments.map(department => ({
    label: department.label,
    value: department.value,
    disabled: existingTypes.includes(department.value.toUpperCase())
  }));

  const newType =
    departmentType === "pre-configured"
      ? preConfiguredDepartment
      : customDepartmentType;

  const newLabel =
    departmentType === "pre-configured"
      ? defaultDepartments.find(d => d.value === preConfiguredDepartment)
          ?.label || ""
      : sanitizeLabel(customDepartmentLabel);

  const hasChanges = selected.type !== newType || selected.label !== newLabel;

  const validation = useValidateDepartment(
    departmentType,
    customDepartmentLabel,
    preConfiguredDepartment,
    existingTypes,
    defaultDepartmentTypes
  );

  return (
    <Modal
      footer={{
        primary: {
          label: "Update",
          disabled: mutation.isPending || !hasChanges || !validation.isValid,
          action: () => {
            setErrorMessages([]); // Clear previous errors
            mutation
              .mutateAsync({
                accountId: accountEntity.id,
                oldType: selected.type,
                newType,
                newLabel,
                userId
              })
              .then(() => hideModal())
              .catch(async error => {
                console.error("Failed to update department:", error);
                setErrorMessages(
                  await parseErrorMessages(
                    error,
                    "Failed to update department. Please try again."
                  )
                );
              });
          },
          isLoading: mutation.isPending
        },
        options: {
          label: "Cancel",
          action: () => hideModal(),
          disabled: mutation.isPending
        }
      }}
      header="Edit Department"
      show={modalToShow === "edit"}
      size="large"
      onHide={() => !mutation.isPending && hideModal()}
    >
      <ModalContent>
        {errorMessages.length > 0 && (
          <Alert sx={{ mb: 2 }} type="error">
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {errorMessages.map(msg => (
                <li key={msg}>{msg}</li>
              ))}
            </Box>
          </Alert>
        )}
        {!isUsingAPreConfiguredDepartment && (
          <RadioButtonList.RadioButtonList
            inline
            data-testid="departmentType"
            displayLabel={false}
            name="departmentType"
            options={[
              {
                label: "Pre-Configured Departments",
                value: "pre-configured"
              },
              {
                label: "Custom Department",
                value: "custom"
              }
            ]}
            sx={{
              "& .MuiFormGroup-root": {
                gap: 1
              }
            }}
            value={departmentType}
            onChange={e =>
              setDepartmentType(e.target.value as "custom" | "pre-configured")
            }
          />
        )}

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid size={{ sm: 12 }}>
            {departmentType === "pre-configured" ? (
              <SelectInput
                disabled={mutation.isPending || isUsingAPreConfiguredDepartment}
                label="Pre-Configured Department"
                options={
                  isUsingAPreConfiguredDepartment
                    ? defaultDepartmentOptions.filter(
                        o => o.value === selected.type
                      )
                    : defaultDepartmentOptions
                }
                value={preConfiguredDepartment}
                onChange={e => setPreConfiguredDepartment(e.target.value ?? "")}
              />
            ) : (
              <>
                <TextInput
                  disabled={mutation.isPending}
                  getError={validateDepartment(
                    existingTypes,
                    defaultDepartmentTypes
                  )}
                  id="customDepartmentLabel"
                  label="Custom Department Label"
                  name="customDepartmentLabel"
                  value={customDepartmentLabel}
                  onChange={e => setCustomDepartmentLabel(e.target.value)}
                />
                {customDepartmentLabel && (
                  <Box
                    sx={{
                      mt: 1,
                      fontSize: "0.875rem",
                      color: "text.secondary",
                      wordBreak: "break-word",
                      overflowWrap: "break-word"
                    }}
                  >
                    Type will be: <strong>{customDepartmentType}</strong>
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </ModalContent>
    </Modal>
  );
};
